import type { WorkflowInput } from "@/stores/workflowsStore";

/**
 * Trigger types that fire automatically with no human in the loop. A workflow
 * rooted in one of these cannot accept runtime inputs from a Run modal — when
 * the schedule/event fires, there is no user to provide values.
 *
 * ⚠️ **DUPLICATION ALERT — READ BEFORE EDITING**
 *
 * This list duplicates `autoFiringTriggerTypes` in the Go backend at
 * `services/b3os-workflow/internal/app/api/validator/definition_warnings.go`.
 * The duplication is intentional but temporary: the frontend check runs in the
 * Run modal without a publish round-trip, so the warning is visible as the user
 * edits instead of only after publish. The proper fix is to have the backend
 * surface its warning array through a lighter endpoint (or via the existing
 * AI chat SSE stream) and delete this file. Tracked as part of
 * [B3-3608](https://linear.app/npclabs/issue/B3-3608).
 *
 * **If you add a trigger type to the backend list, add it here too.** A drift
 * test comparing the two lists would catch this at CI time — it's a good
 * follow-up once the two layers aren't both being iterated on.
 *
 * Excluded intentionally:
 *   - `manual` — does accept Run-modal input
 *   - webhook triggers — receive data via the request body, different access pattern
 */
export const AUTO_FIRING_TRIGGER_TYPES: ReadonlySet<string> = new Set([
  "cronjob",
  "schedule",
  "token-price-cexes",
  "evm-log",
  "erc20-receive",
  "erc20-send",
  "eth-receive",
  "eth-send",
  "polymarket-user-bet",
  "polymarket-new-market",
  "solana-transaction",
  "exchange-listing",
]);

/**
 * isAutoFiringTrigger reports whether a given trigger type fires automatically
 * with no user interaction. Shared helper so callers don't have to reach into
 * the AUTO_FIRING_TRIGGER_TYPES set directly and miss future signature changes.
 */
export function isAutoFiringTrigger(triggerType: string | undefined): boolean {
  if (!triggerType) return false;
  return AUTO_FIRING_TRIGGER_TYPES.has(triggerType);
}

/**
 * Result of the auto-trigger input check. Mirrors the backend
 * `WorkflowWarningItem` shape from `schemas/publish_workflow.go` so the same
 * banner UI can be driven by either source.
 */
export interface AutoTriggerInputWarning {
  code: "auto_trigger_with_unprovided_runtime_inputs";
  message: string;
  fields: string[];
}

/**
 * computeAutoTriggerInputWarning returns a warning when:
 *   1. The root trigger type is auto-firing.
 *   2. The workflow has at least one required inputSchema entry.
 *
 * The Run modal renders the result as a yellow banner. This is the same logic
 * the backend validator runs at publish time, but recomputed client-side so
 * the warning is visible immediately as the user edits — no publish round-trip
 * needed.
 *
 * Returns null when the workflow does not match the warning shape.
 *
 * Note: this client-side helper does NOT walk node payloads to confirm that
 * the inputSchema fields are actually referenced — that's the deeper backend
 * check (which uses the full WorkflowDefinition the FE doesn't always have in
 * the Run modal context). For the user-facing warning we accept the looser
 * check: any auto-firing trigger with required inputs is suspicious enough to
 * surface.
 */
export function computeAutoTriggerInputWarning(
  triggerType: string,
  inputSchema: WorkflowInput[] | undefined,
): AutoTriggerInputWarning | null {
  if (!triggerType || !AUTO_FIRING_TRIGGER_TYPES.has(triggerType)) {
    return null;
  }
  if (!inputSchema || inputSchema.length === 0) {
    return null;
  }
  const requiredFields = inputSchema.filter(input => input.required && input.key).map(input => input.key);
  if (requiredFields.length === 0) {
    return null;
  }

  return {
    code: "auto_trigger_with_unprovided_runtime_inputs",
    message:
      `This workflow's trigger (${triggerType}) fires automatically — there is no user to provide ` +
      `runtime inputs when the schedule or event fires. The required input(s) below will be undefined ` +
      `at runtime. Either remove them from the workflow, set defaults, or change the trigger to manual.`,
    fields: requiredFields,
  };
}
